import { ThemeConfig } from 'src/app/@data-services/event-config/event-theme-config.data-model';

/**
 * Default Rozie Synopsis brand theme colors
 * Based on brand guidelines: https://rozie-brand-guidelines.com
 *
 * This file provides TypeScript theme objects applied as CSS custom properties
 * via apply-theme.ts utility. The runtime CSS custom properties written by
 * apply-theme.ts are the single source of truth for theme colors; SCSS reads
 * them via var(--…) rather than duplicating the values.
 *
 * Brand Colors:
 * - Primary (Magenta): #d92d8a
 * - Secondary (Orange): #f58020
 * - Success (Green): #008933
 * - Warning/Error (Red): #ff3030
 */
export const DEFAULT_ROZIE_THEME: ThemeConfig = {
  Light: {
    Colors: {
      // Primary brand color (Magenta)
      Primary: '#d92d8a',
      Accent: '#f58020',
      Warn: '#ff9800',
      Success: '#008933',
      Error: '#ff3030',

      // Gradient colors for hero/background
      PrimaryGradientStart: '#d92d8a',
      PrimaryGradientEnd: '#f58020',
      PrimaryColorVariant2: '#b82675',

      // Card hover gradients
      CardGradientHoverStart: '#ffffff',
      CardGradientHoverEnd: '#fff8fb',

      // Background patches for decorative elements
      BackgroundPatchLeftTop: '#d92d8a',
      BackgroundPatchLeftMiddle: '#f58020',
      BackgroundPatchRightMiddle: '#54ecd0',
      BackgroundPatchGradientBottomStart: '#d92d8a',
      BackgroundPatchGradientBottomEnd: '#f58020',

      // Upcoming gradient colors
      SessionUpcomingGradientStart: '#f58020',
      SessionUpcomingGradientEnd: '#ff9800',

      // Live gradient colors
      SessionLiveGradientStart: '#d92d8a',
      SessionLiveGradientEnd: '#d92d8a',

      // Replay gradient colors
      SessionReplayGradientStart: '#d92d8a',
      SessionReplayGradientEnd: '#d92d8a',

      // Debrief gradient colors
      SessionDebriefGradientStart: '#d92d8a',
      SessionDebriefGradientEnd: '#d92d8a',

      // Card colors
      SessionInsightCardHeaderGradientStart: '#d92d8a',
      SessionInsightCardHeaderGradientEnd: '#f58020',

      // Bottom sheet accent (CTA buttons)
      BottomSheetAccentGradientStart: '#d92d8a',
      BottomSheetAccentGradientEnd: '#b82675',

      // Session agenda tab colors
      SessionAgendaTabSelectedGradientStart: '#d92d8a',
      SessionAgendaTabSelectedGradientEnd: '#f58020',
      SessionAgendaTabHoverBackground: '#f9f9f9',
      SessionAgendaTabSelectedBackground: '#ffffff',
    },
    BackgroundImage: 'url("/assets/new_background_default.svg")',
    ShowOverlay: true,
  },
};

/**
 * Rozie brand color tokens for easy reference
 * Use these for consistent theming across components
 */
export const ROZIE_BRAND_COLORS = {
  // Primary colors
  primary: '#d92d8a', // Brand Magenta
  primaryDark: '#b82675', // Hover state
  primaryLight: '#fff2f7', // Light tint

  // Accent colors
  accent: '#f58020', // Amber/Orange
  accentLight: '#ffb74d',

  // Semantic colors
  success: '#008933',
  error: '#ff3030',
  warning: '#ff9800',
  info: '#aa6615',

  // Neutral colors
  dark: '#1e2225',
  body: '#475258',
  muted: '#6b7886',
  placeholder: '#7a8896',
  border: '#cfdae6',
  raised: '#f5f7fa',
  surface: '#ffffff',

  // Gradients
  gradientBrandWarm: 'linear-gradient(160deg, #e8006f, #f58020)',
  gradientMagentaDepth: 'linear-gradient(160deg, #d92d8a, #b82675)',
  gradientAmberFire: 'linear-gradient(160deg, #f58020, #e65100)',
} as const;
